import { z } from 'zod'

import {
  ConsumerSearchType,
  SEARCH_CONSUMER_QUERY_MAX_LEN,
  SearchConsumerErrorMessage,
} from '../constants/searchConsumer'

export const consumerSearchTypeSchema = z.enum([
  ConsumerSearchType.VENUE,
  ConsumerSearchType.EVENT,
  ConsumerSearchType.CURATED_LIST,
])

export const searchConsumerTypeOptionSchema = z.object({
  name: z.string(),
  value: consumerSearchTypeSchema,
})

function firstQueryParam(v: unknown): unknown {
  if (Array.isArray(v)) return v[0]
  return v
}

export const searchConsumerHttpQuerySchema = z.object({
  type: z.preprocess((v) => {
    if (v == null || v === '') return []
    const arr = Array.isArray(v) ? v : [v]
    return arr.map((x) => (x == null ? '' : String(x))).filter(Boolean)
  }, z.array(consumerSearchTypeSchema).nonempty()),
  query: z.preprocess(
    (v) => {
      const f = firstQueryParam(v)
      if (f === undefined || f === null || f === '') return undefined
      return String(f)
    },
    z
      .string()
      .optional()
      .refine(
        (s) => s === undefined || s.length <= SEARCH_CONSUMER_QUERY_MAX_LEN,
        { message: SearchConsumerErrorMessage.INVALID_QUERY }
      )
  ),
  limit: z.preprocess((v) => {
    const f = firstQueryParam(v)
    if (f === undefined || f === null || f === '') return undefined
    return f
  }, z.coerce.number().int().positive().max(100).optional()),
  cityId: z.preprocess((v) => {
    const f = firstQueryParam(v)
    if (f === undefined || f === null || f === '') return undefined
    return String(f)
  }, z.string().optional()),
})

export const searchConsumerVenueCardSchema = z.object({
  id: z.string(),
  name: z.string(),
  location: z.unknown(),
  images: z.unknown(),
  seoPathname: z.string().optional().nullable(),
})

export const searchConsumerVenueHitSchema = z.object({
  id: z.string(),
  name: z.string(),
  highlightName: z.string(),
  imageUrl: z.string().optional().nullable(),
  link: z.unknown(),
  venue: searchConsumerVenueCardSchema,
})

export const searchConsumerEventCardSchema = z.object({
  id: z.string(),
  name: z.string(),
  date: z.unknown(),
  images: z.unknown(),
  venue: z.unknown(),
  city: z.unknown(),
  seoPathname: z.string().optional().nullable(),
  performers: z.unknown().optional(),
})

export const searchConsumerEventHitSchema = z.object({
  id: z.string(),
  name: z.string(),
  highlightName: z.string(),
  highlightPerformers: z.union([z.string(), z.array(z.string())]).optional(),
  imageUrl: z.string().optional().nullable(),
  link: z.unknown(),
  event: searchConsumerEventCardSchema,
})

export const searchConsumerCuratedListCardSchema = z.object({
  id: z.string(),
  title: z.string(),
  imageUrl: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  city: z.unknown(),
  followCount: z.number().optional().nullable(),
  itemCount: z.number().optional().nullable(),
})

export const searchConsumerCuratedListHitSchema = z.object({
  id: z.string(),
  name: z.string(),
  highlightName: z.string(),
  imageUrl: z.string().optional().nullable(),
  link: z.unknown(),
  curatedList: searchConsumerCuratedListCardSchema,
})

export const searchConsumerResponseSchema = z.object({
  VENUE: z.array(searchConsumerVenueHitSchema).optional(),
  EVENT: z.array(searchConsumerEventHitSchema).optional(),
  CURATED_LIST: z.array(searchConsumerCuratedListHitSchema).optional(),
})
